<script setup lang="ts">
// A caster input's local links (CasterStats; DECISIONS 2026-10-12 "caster
// links on the node's own network"): made on the node, for a caster on its
// network, with no central or internet in the way. The node serves the page
// on its own HTTPS port with a self-signed certificate; a link is shown once,
// as it is made (the node keeps its token hashed).
import { ref, watch } from 'vue'
import { api, type CastLink, type CastLinks } from '../../api'
import { useNode } from '../../store'

const props = defineProps<{ inputId: string }>()
const node = useNode()
const state = ref<CastLinks | null>(null)
const forWho = ref('')
const days = ref(1)
const made = ref<CastLink | null>(null) // the one just made, its links shown until dismissed
async function load() {
    try {
        state.value = await api.castLinks(props.inputId)
    } catch {
        state.value = null // a node from before local links
    }
}
watch(
    () => props.inputId,
    () => {
        made.value = null
        void load()
    },
    { immediate: true },
)
async function make() {
    try {
        const l = await api.makeCastLink(props.inputId, { name: forWho.value.trim(), hours: days.value * 24 })
        made.value = l
        forWho.value = ''
        const first = l.urls?.[0]
        if (first) await node.copy(first)
        await load()
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    }
}
async function revoke(l: CastLink) {
    try {
        await api.revokeCastLink(props.inputId, l.id)
        if (made.value?.id === l.id) made.value = null
        await load()
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    }
}
const until = (t: string) =>
    new Date(t).toLocaleString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    })
</script>

<template>
    <details v-if="state" class="border-t border-line px-2.5 py-2" data-test="local-links">
        <summary class="my-0 subhead">
            Local links{{ state.links.length ? ' · ' + state.links.length + ' active' : '' }}
        </summary>
        <p class="mt-1 text-fg-2">
            For a caster on this node's own network: no central or internet needed. Their browser warns once about the
            node's own certificate (Advanced, then Proceed).
        </p>
        <div v-if="!state.port" class="mt-1 text-warn">
            off on this node: noded serves them on its caster HTTPS port (-cast-tls-port), which is 0 or was taken
        </div>
        <template v-else>
            <form class="mt-2 flex flex-wrap items-center gap-1.5" @submit.prevent="make">
                <input
                    v-model="forWho"
                    required
                    maxlength="100"
                    placeholder="who it is for"
                    class="min-w-0 flex-1 text-xs"
                    data-test="local-link-name"
                />
                <select v-model.number="days" class="w-auto text-xs" aria-label="Valid for">
                    <option :value="1">1 day</option>
                    <option :value="3">3 days</option>
                    <option :value="7">a week</option>
                    <option :value="30">30 days</option>
                </select>
                <button
                    class="btn-primary text-xs"
                    title="The link is copied as it is made"
                    data-test="local-link-make"
                >
                    Make local link
                </button>
            </form>
            <div
                v-if="made?.urls?.length"
                class="mt-2 rounded border border-line bg-bg-2 p-2"
                data-test="local-link-made"
            >
                <div class="flex items-start gap-1.5">
                    <span class="min-w-0 flex-1 font-medium"
                        >For {{ made.name }}: copy it now, it is not shown again</span
                    >
                    <button class="btn-ghost px-1.5 py-0 text-xs" @click="made = null">Done</button>
                </div>
                <div v-for="u in made.urls" :key="u" class="mt-1 flex items-center gap-1.5">
                    <code class="min-w-0 flex-1 truncate" :title="u">{{ u }}</code>
                    <button class="btn-ghost px-2 py-0.5 text-xs" @click="node.copy(u)">Copy</button>
                </div>
                <div v-if="made.urls.length > 1" class="mt-1 text-fg-3">
                    one per address of the node: give the one on the caster's network
                </div>
            </div>
        </template>
        <table v-if="state.links.length" class="mt-2 w-full text-xs">
            <tr v-for="l in state.links" :key="l.id" class="border-t border-line">
                <td class="py-1.5 pr-2 font-medium">{{ l.name }}</td>
                <td class="pr-2 text-fg-2">until {{ until(l.expires_at) }}</td>
                <td class="py-1.5 text-right">
                    <button class="btn-danger px-2 py-0.5 text-xs" @click="revoke(l)">Revoke</button>
                </td>
            </tr>
        </table>
    </details>
</template>
